import { useState, useEffect, useCallback } from 'react';
import { calendarAPI, usersAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Plus, X, Mail, Users, Search } from 'lucide-react';

function defaultStart() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.toISOString().slice(0, 16);
}
function plusOneHour(iso) {
  const d = new Date(iso);
  d.setHours(d.getHours() + 1);
  return d.toISOString().slice(0, 16);
}

export default function EventDialog({ open, onOpenChange, onSaved, initialDate }) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    start_at: defaultStart(),
    end_at: '',
    attendee_user_ids: [],
    attendee_emails: [],
    send_invite: true,
  });

  const loadUsers = useCallback(async () => {
    try {
      const [agents, admins, customers] = await Promise.all([
        usersAPI.list({ role: 'agent' }),
        usersAPI.list({ role: 'admin' }),
        usersAPI.list({ role: 'customer' }),
      ]);
      const all = [...(agents.data || []), ...(admins.data || []), ...(customers.data || [])];
      const seen = new Set();
      setUsers(all.filter((u) => (u.email && !seen.has(u.id)) ? (seen.add(u.id), true) : false));
    } catch (err) {
      console.error('Failed to load users', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadUsers();
      const start = initialDate
        ? (() => { const d = new Date(initialDate); d.setHours(9, 0, 0, 0); return d.toISOString().slice(0, 16); })()
        : defaultStart();
      setForm({
        title: '',
        description: '',
        location: '',
        start_at: start,
        end_at: '',
        attendee_user_ids: [],
        attendee_emails: [],
        send_invite: true,
      });
      setUserSearch('');
      setExternalEmail('');
    }
  }, [open, initialDate, loadUsers]);

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const toggleUser = (uid) => {
    setForm((f) => ({
      ...f,
      attendee_user_ids: f.attendee_user_ids.includes(uid)
        ? f.attendee_user_ids.filter((x) => x !== uid)
        : [...f.attendee_user_ids, uid],
    }));
  };

  const addExternalEmail = () => {
    const v = externalEmail.trim().toLowerCase();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
    if (form.attendee_emails.includes(v)) return;
    setForm({ ...form, attendee_emails: [...form.attendee_emails, v] });
    setExternalEmail('');
  };

  const removeExternalEmail = (em) => {
    setForm({ ...form, attendee_emails: form.attendee_emails.filter((x) => x !== em) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_at) return;
    try {
      setLoading(true);
      const payload = {
        title: form.title.trim(),
        description: form.description || undefined,
        location: form.location || undefined,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : new Date(plusOneHour(form.start_at)).toISOString(),
        attendee_user_ids: form.attendee_user_ids,
        attendee_emails: form.attendee_emails,
        send_invite: form.send_invite,
      };
      const res = await calendarAPI.createEvent(payload);
      onOpenChange(false);
      onSaved?.(res.data);
      // Show invite result toast-like alert
      const inv = res.data?.invite;
      if (form.send_invite) {
        if (inv?.status === 'success') {
          // ok
        } else if (inv?.status === 'skipped') {
          alert('Event created. Email invite was skipped — Zepto Mail is not configured. Configure it in Settings → Email.');
        } else if (inv?.status === 'error') {
          alert(`Event created, but the email invite failed: ${inv.code || ''} ${typeof inv.data === 'string' ? inv.data : JSON.stringify(inv.data || {}).slice(0, 200)}`);
        }
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const totalAttendees = form.attendee_user_ids.length + form.attendee_emails.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-800">Add Calendar Event</DialogTitle>
          <DialogDescription className="text-slate-500">
            Schedule a meeting or event and optionally email an .ics invitation to attendees.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-1.5">
            <Label htmlFor="event-title" className="text-slate-700">Title <span className="text-red-500">*</span></Label>
            <Input id="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Quarterly review" className="bg-white border-slate-200 h-11" required data-testid="event-title-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="event-start" className="text-slate-700">Start <span className="text-red-500">*</span></Label>
              <Input id="event-start" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="bg-white border-slate-200 h-11" required data-testid="event-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event-end" className="text-slate-700">End <span className="text-slate-400 font-normal">(defaults to +1h)</span></Label>
              <Input id="event-end" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="bg-white border-slate-200 h-11" data-testid="event-end-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-location" className="text-slate-700">Location</Label>
            <Input id="event-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Toronto HQ - Room 302 / Zoom" className="bg-white border-slate-200 h-11" data-testid="event-location-input" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event-desc" className="text-slate-700">Description</Label>
            <Textarea id="event-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Agenda / notes" className="bg-white border-slate-200 min-h-[80px]" data-testid="event-description-input" />
          </div>

          {/* Attendees */}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <Label className="text-slate-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />Attendees
              {totalAttendees > 0 && <Badge variant="outline" className="border-slate-200 text-slate-600">{totalAttendees}</Badge>}
            </Label>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search team members..." className="pl-10 h-10 bg-white border-slate-200" data-testid="event-user-search" />
            </div>

            <ScrollArea className="h-40 border border-slate-200 rounded-lg bg-slate-50/40">
              <ul className="p-2 space-y-1">
                {filteredUsers.length === 0 ? (
                  <li className="text-xs text-slate-400 text-center py-4">No matching users</li>
                ) : filteredUsers.slice(0, 50).map((u) => (
                  <li key={u.id}>
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white cursor-pointer">
                      <Checkbox
                        checked={form.attendee_user_ids.includes(u.id)}
                        onCheckedChange={() => toggleUser(u.id)}
                        data-testid={`event-attendee-${u.id}`}
                      />
                      <span className="text-sm text-slate-800 flex-1 truncate">{u.name}</span>
                      <span className="text-xs text-slate-400 truncate">{u.email}</span>
                      <Badge variant="outline" className="text-[10px] capitalize border-slate-200 text-slate-500">{u.role}</Badge>
                    </label>
                  </li>
                ))}
              </ul>
            </ScrollArea>

            {/* External emails */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Add by email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={externalEmail}
                  onChange={(e) => setExternalEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExternalEmail(); } }}
                  placeholder="external@example.com"
                  className="bg-white border-slate-200 h-10"
                  data-testid="event-external-email-input"
                />
                <Button type="button" variant="outline" className="rounded-full h-10 px-4" onClick={addExternalEmail} data-testid="event-add-external-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add
                </Button>
              </div>
              {form.attendee_emails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.attendee_emails.map((em) => (
                    <Badge key={em} variant="outline" className="border-slate-200 text-slate-700 text-[11px] py-1 pl-2.5 pr-1 rounded-full">
                      <Mail className="w-3 h-3 mr-1 text-slate-400" />{em}
                      <button type="button" onClick={() => removeExternalEmail(em)} className="ml-1 w-4 h-4 rounded-full hover:bg-slate-200 inline-flex items-center justify-center" aria-label={`Remove ${em}`} data-testid={`event-remove-email-${em}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-cyan-50/50 border border-cyan-100">
              <Checkbox id="send-invite" checked={form.send_invite} onCheckedChange={(v) => setForm({ ...form, send_invite: !!v })} data-testid="event-send-invite-checkbox" />
              <Label htmlFor="send-invite" className="cursor-pointer text-sm text-slate-700 flex-1">
                Send email invitation to attendees
                <span className="block text-xs text-slate-500 mt-0.5">Includes a calendar (.ics) attachment via Zepto Mail.</span>
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-600">Cancel</Button>
            <Button type="submit" disabled={loading || !form.title.trim()} className="rounded-full bg-cyan-500 hover:bg-cyan-600 h-11 px-6" data-testid="event-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create event'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
